import * as Yup from 'yup';

import { Form, Formik, FormikHelpers } from 'formik';
import { Notification, NotificationType } from 'modules/core/domain/interfaces/supabase/notifications/types';

import NotificationPreview from './notification-preview';
import React, { useState } from 'react';
import UDButton from 'ud-ui/components/button';
import UDFormInput from 'ud-form/components/input';
import UDFormSelect from 'ud-form/components/select';
import UDFormTextarea from 'ud-form/components/input/textarea';
import { toast } from 'react-toastify';
import { typeOptions } from 'modules/notifications/domain/const';
import { usePreventNavigation } from 'shared/hooks';
import { Pages } from 'modules/navigation/domain/enums/pages';
import _ from 'lodash';

type TypeOption = (typeof typeOptions)[number];

export interface FormValues {
  type: TypeOption | null;
  title: string;
  text: string;
  userId: number | null | undefined;
}

const validationSchema = Yup.object().shape({
  type: Yup.object().required('Выберите тип уведомления'),
  title: Yup.string().required('Введите заголовок').trim(),
  text: Yup.string().required('Введите текст').trim(),
  userId: Yup.number().when('type', {
    is: (type: TypeOption | null) => type?.value === 'user_event',
    then: (schema) => schema.required('Для события пользователя необходимо указать ID'),
    otherwise: (schema) => schema.nullable().optional(),
  }),
});

interface Props {
  initialValues?: Partial<Notification>;
  onSubmit: (values: Partial<Notification>) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
}

const NotificationForm: React.FC<Props> = ({ initialValues, onSubmit, onCancel, loading = false }) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    try {
      const trimmedValues = {
        ...values,
        type: values.type?.value as NotificationType,
        title: values.title?.trim(),
        text: values.text?.trim(),
      };
      await onSubmit(trimmedValues);
      helpers.setSubmitting(false);
    } catch (error) {
      console.error('Ошибка при сохранении уведомления:', error);
      toast.error('Произошла ошибка при сохранении уведомления');
    }
  };

  const initialType = initialValues?.type ? (typeOptions.find((opt) => opt.value === initialValues.type) ?? null) : null;

  let initialValues1 = {
    type: initialType ?? typeOptions.find((opt) => opt.value === 'info') ?? null,
    title: initialValues?.title ?? '',
    text: initialValues?.text ?? '',
    userId: initialValues?.userId,
  };

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.TEAMS.INDEX,
  });

  return (
    <Formik
      initialValues={initialValues1}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values, formikHelpers) => {
        handleSubmit(values, formikHelpers);
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {({ isSubmitting, values, errors, touched }) => {
        const previewValues = {
          ...values,
          type: values.type?.value as NotificationType,
        };

        const showUserIdField = values.type?.value === 'user_event';

        return (
          <>
            <Form className="flex flex-col w-1/2 gap-4">
              <UDFormSelect
                name="type"
                label="Тип уведомления"
                required
                options={typeOptions.filter((option) => option.value !== 'all')}
                errorVisible={!!(touched.type && errors.type)}
              />

              <UDFormInput
                name="title"
                label="Заголовок"
                required
                placeholder="Введите заголовок уведомления"
                errorVisible={!!(touched.title && errors.title)}
              />

              <UDFormTextarea
                name="text"
                label="Текст"
                required
                placeholder="Введите текст уведомления"
                rows={5}
                style={{ resize: 'vertical', minHeight: '120px' }}
                errorVisible={!!(touched.text && errors.text)}
              />

              {showUserIdField && (
                <UDFormInput
                  name="userId"
                  label="ID пользователя"
                  type="number"
                  required
                  placeholder="Введите ID пользователя"
                  errorVisible={!!(touched.userId && errors.userId)}
                />
              )}

              <div className="flex justify-start mt-4 gap-3">
                <UDButton variant="primary" type="submit" loading={isSubmitting || loading} className="w-full">
                  {initialValues ? 'Сохранить' : 'Создать'}
                </UDButton>
                {onCancel && (
                  <UDButton
                    variant="secondary"
                    onClick={onCancel ?? backHandler}
                    disabled={isSubmitting || loading}
                    loading={false}
                    className="w-full"
                  >
                    Отмена
                  </UDButton>
                )}
              </div>
            </Form>

            <NotificationPreview notification={previewValues} />
            <PreventNavigationModal />
          </>
        );
      }}
    </Formik>
  );
};

export default NotificationForm;
